import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    VStack,
    HStack,
    Badge,
    
    Text,
    useDisclosure,
    Select,
    SimpleGrid,
    Icon,
    Switch
} from '@chakra-ui/react';
import { FiPlus, FiEdit, FiTrash2, FiBook, FiBookOpen, FiAward, FiTarget, FiTrendingUp } from 'react-icons/fi';
import * as FiIcons from 'react-icons/fi';
import niveauService from '../services/niveauService';

const iconOptions = [
    { name: 'FiBook', component: FiBook },
    { name: 'FiBookOpen', component: FiBookOpen },
    { name: 'FiAward', component: FiAward },
    { name: 'FiTarget', component: FiTarget },
    { name: 'FiTrendingUp', component: FiTrendingUp }
];

const colorOptions = [
    '#3182CE', // blue
    '#38A169', // green
    '#DD6B20', // orange
    '#9F7AEA', // purple
    '#E53E3E', // red
    '#D69E2E', // yellow
    '#319795', // teal
    '#ED64A6'  // pink
];

const NiveauManagement = () => {
    const [niveaux, setNiveaux] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNiveau, setSelectedNiveau] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const [formData, setFormData] = useState({
        name: '',
        color: '#3182CE',
        icon: 'FiBook',
        matieres: [],
        order: 0,
        active: true
    });

    const [newMatiere, setNewMatiere] = useState({ name: '', icon: 'FiBookOpen', prix: 0, videoUrl: '' });

    useEffect(() => {
        fetchNiveaux();
    }, []);

    const fetchNiveaux = async () => {
        try {
            const response = await niveauService.getAllNiveaux();
            setNiveaux(response.data);
        } catch (error) {
            toast({
                title: 'خطأ في تحميل البيانات',
                status: 'error',
                duration: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (niveau = null) => {
        if (niveau) {
            setIsEditing(true);
            setSelectedNiveau(niveau);
            setFormData({
                name: niveau.name,
                color: niveau.color,
                icon: niveau.icon,
                matieres: niveau.matieres || [],
                order: niveau.order,
                active: niveau.active
            });
        } else {
            setIsEditing(false);
            setSelectedNiveau(null);
            setFormData({
                name: '',
                color: '#3182CE',
                icon: 'FiBook',
                matieres: [],
                order: niveaux.length,
                active: true
            });
        }
        onOpen();
    };

    const handleSave = async () => {
        if (!formData.name) {
            toast({
                title: 'يرجى إدخال اسم المستوى',
                status: 'warning',
                duration: 3000
            });
            return;
        }

        try {
            if (isEditing) {
                await niveauService.updateNiveau(selectedNiveau._id, formData);
                toast({
                    title: 'تم التحديث بنجاح',
                    status: 'success',
                    duration: 3000
                });
            } else {
                await niveauService.createNiveau(formData);
                toast({
                    title: 'تم الإنشاء بنجاح',
                    status: 'success',
                    duration: 3000
                });
            }
            fetchNiveaux();
            onClose();
        } catch (error) {
            toast({
                title: 'خطأ في الحفظ',
                description: error.response?.data?.message,
                status: 'error',
                duration: 3000
            });
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا المستوى؟')) {
            try {
                await niveauService.deleteNiveau(id);
                toast({
                    title: 'تم الحذف بنجاح',
                    status: 'success',
                    duration: 3000
                });
                fetchNiveaux();
            } catch (error) {
                toast({
                    title: 'خطأ في الحذف',
                    status: 'error',
                    duration: 3000
                });
            }
        }
    };

    const handleAddMatiere = () => {
        if (!newMatiere.name) {
            toast({
                title: 'يرجى إدخال اسم المادة',
                status: 'warning',
                duration: 2000
            });
            return;
        }
        setFormData({
            ...formData,
            matieres: [...formData.matieres, newMatiere]
        });
        setNewMatiere({ name: '', icon: 'FiBookOpen', prix: 0, videoUrl: '' });
    };

    const handleRemoveMatiere = (index) => {
        setFormData({
            ...formData,
            matieres: formData.matieres.filter((_, i) => i !== index)
        });
    };

    const getIconComponent = (iconName) => {
        return FiIcons[iconName] || FiBook;
    };

    if (loading) {
        return (
            <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
                <CobraSpinner size="xl" color="blue.500" />
            </Box>
        );
    }

    return (
        <Box w="100%">
            <HStack justify="space-between" mb={8}>
                <Heading size="xl" fontFamily="Tajawal, sans-serif">
                    إدارة المستويات ({niveaux.length})
                </Heading>
                <Button
                    leftIcon={<FiPlus />}
                    colorScheme="blue"
                    onClick={() => handleOpenModal()}
                    fontFamily="Tajawal, sans-serif"
                    size="lg"
                >
                    إضافة مستوى جديد
                </Button>
            </HStack>

            <Box bg="white" borderRadius="xl" boxShadow="lg" overflow="hidden">
                <Table variant="simple">
                    <Thead bg="gray.50">
                        <Tr>
                            <Th fontFamily="Tajawal, sans-serif">الاسم</Th>
                            <Th fontFamily="Tajawal, sans-serif">اللون</Th>
                            <Th fontFamily="Tajawal, sans-serif">الأيقونة</Th>
                            <Th fontFamily="Tajawal, sans-serif">عدد المواد</Th>
                            <Th fontFamily="Tajawal, sans-serif">الترتيب</Th>
                            <Th fontFamily="Tajawal, sans-serif">الحالة</Th>
                            <Th fontFamily="Tajawal, sans-serif">إجراءات</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {niveaux.map((niveau) => {
                            const IconComp = getIconComponent(niveau.icon);
                            return (
                                <Tr key={niveau._id} _hover={{ bg: 'gray.50' }}>
                                    <Td fontFamily="Tajawal, sans-serif" fontWeight="600">{niveau.name}</Td>
                                    <Td>
                                        <Box
                                            w="40px"
                                            h="40px"
                                            bg={niveau.color}
                                            borderRadius="md"
                                            boxShadow="sm"
                                        />
                                    </Td>
                                    <Td>
                                        <Icon as={IconComp} boxSize={6} color={niveau.color} />
                                    </Td>
                                    <Td>
                                        <Badge colorScheme="purple">
                                            {niveau.matieres?.length || 0} مادة
                                        </Badge>
                                    </Td>
                                    <Td fontFamily="Tajawal, sans-serif">{niveau.order}</Td>
                                    <Td>
                                        <Badge colorScheme={niveau.active ? 'green' : 'red'}>
                                            {niveau.active ? 'نشط' : 'غير نشط'}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <HStack spacing={2}>
                                            <IconButton
                                                icon={<FiEdit />}
                                                size="sm"
                                                colorScheme="blue"
                                                variant="ghost"
                                                onClick={() => handleOpenModal(niveau)}
                                            />
                                            <IconButton
                                                icon={<FiTrash2 />}
                                                size="sm"
                                                colorScheme="red"
                                                variant="ghost"
                                                onClick={() => handleDelete(niveau._id)}
                                            />
                                        </HStack>
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>

                {niveaux.length === 0 && (
                    <Box textAlign="center" py={12}>
                        <Text fontSize="lg" color="gray.500" fontFamily="Tajawal, sans-serif">
                            لا توجد مستويات. ابدأ بإضافة مستوى جديد
                        </Text>
                    </Box>
                )}
            </Box>

            <Modal isOpen={isOpen} onClose={onClose} size="2xl">
                <ModalOverlay />
                <ModalContent dir="rtl">
                    <ModalHeader fontFamily="Tajawal, sans-serif">
                        {isEditing ? 'تعديل المستوى' : 'إضافة مستوى جديد'}
                    </ModalHeader>
                    <ModalCloseButton left="12px" right="unset" />
                    <ModalBody>
                        <VStack spacing={6}>
                            <FormControl isRequired>
                                <FormLabel fontFamily="Tajawal, sans-serif">اسم المستوى</FormLabel>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="مثال: 7ème"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontFamily="Tajawal, sans-serif">اللون</FormLabel>
                                <SimpleGrid columns={8} spacing={3}>
                                    {colorOptions.map((color) => (
                                        <Box
                                            key={color}
                                            w="40px"
                                            h="40px"
                                            bg={color}
                                            borderRadius="md"
                                            cursor="pointer"
                                            border={formData.color === color ? '3px solid' : '1px solid'}
                                            borderColor={formData.color === color ? 'black' : 'gray.300'}
                                            onClick={() => setFormData({ ...formData, color })}
                                            _hover={{ transform: 'scale(1.1)' }}
                                            transition="all 0.2s"
                                        />
                                    ))}
                                </SimpleGrid>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontFamily="Tajawal, sans-serif">الأيقونة</FormLabel>
                                <SimpleGrid columns={5} spacing={4}>
                                    {iconOptions.map(({ name, component: IconComp }) => (
                                        <Box
                                            key={name}
                                            p={4}
                                            borderRadius="md"
                                            border={formData.icon === name ? '2px solid' : '1px solid'}
                                            borderColor={formData.icon === name ? formData.color : 'gray.300'}
                                            cursor="pointer"
                                            onClick={() => setFormData({ ...formData, icon: name })}
                                            _hover={{ bg: 'gray.50' }}
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                        >
                                            <Icon as={IconComp} boxSize={8} color={formData.color} />
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontFamily="Tajawal, sans-serif">المواد</FormLabel>
                                <VStack spacing={3} align="stretch">
                                    {formData.matieres.map((matiere, index) => {
                                        const MatiereIcon = getIconComponent(matiere.icon);
                                        return (
                                            <HStack key={index} bg="gray.50" p={3} borderRadius="md">
                                                <Icon as={MatiereIcon} color={formData.color} />
                                                <Text flex={1} fontFamily="Tajawal, sans-serif">{matiere.name}</Text>
                                                <Badge colorScheme="green" fontSize="sm" px={2}>
                                                    {matiere.prix || 0} DT/مو
                                                </Badge>
                                                {matiere.videoUrl && (
                                                    <Badge colorScheme="blue" fontSize="xs" px={2} isTruncated maxW="120px" title={matiere.videoUrl}>
                                                        🎬 فيديو
                                                    </Badge>
                                                )}
                                                <IconButton
                                                    icon={<FiTrash2 />}
                                                    size="sm"
                                                    colorScheme="red"
                                                    variant="ghost"
                                                    onClick={() => handleRemoveMatiere(index)}
                                                />
                                            </HStack>
                                        );
                                    })}
                                    <VStack spacing={2} align="stretch">
                                        <HStack>
                                            <Input
                                                placeholder="اسم المادة"
                                                value={newMatiere.name}
                                                onChange={(e) => setNewMatiere({ ...newMatiere, name: e.target.value })}
                                            />
                                            <Input
                                                placeholder="السعر (DT)"
                                                type="number"
                                                w="120px"
                                                min={0}
                                                value={newMatiere.prix}
                                                onChange={(e) => setNewMatiere({ ...newMatiere, prix: parseFloat(e.target.value) || 0 })}
                                            />
                                            <Select
                                                w="150px"
                                                value={newMatiere.icon}
                                                onChange={(e) => setNewMatiere({ ...newMatiere, icon: e.target.value })}
                                            >
                                                {iconOptions.map(({ name }) => (
                                                    <option key={name} value={name}>{name}</option>
                                                ))}
                                            </Select>
                                        </HStack>
                                        <HStack>
                                            <Input
                                                placeholder="رابط الفيديو (اختياري)"
                                                value={newMatiere.videoUrl}
                                                onChange={(e) => setNewMatiere({ ...newMatiere, videoUrl: e.target.value })}
                                                dir="ltr"
                                            />
                                            <Button onClick={handleAddMatiere} colorScheme="green" flexShrink={0}>
                                                إضافة
                                            </Button>
                                        </HStack>
                                    </VStack>
                                </VStack>
                            </FormControl>

                            <HStack w="100%" justify="space-between">
                                <FormControl w="auto">
                                    <FormLabel fontFamily="Tajawal, sans-serif">نشط</FormLabel>
                                    <Switch
                                        isChecked={formData.active}
                                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                        colorScheme="green"
                                    />
                                </FormControl>
                                <FormControl w="150px">
                                    <FormLabel fontFamily="Tajawal, sans-serif">الترتيب</FormLabel>
                                    <Input
                                        type="number"
                                        value={formData.order}
                                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                    />
                                </FormControl>
                            </HStack>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" ml={3} onClick={onClose} fontFamily="Tajawal, sans-serif">
                            إلغاء
                        </Button>
                        <Button colorScheme="blue" onClick={handleSave} fontFamily="Tajawal, sans-serif">
                            {isEditing ? 'تحديث' : 'إنشاء'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default NiveauManagement;
